/**
 * GENERATED FILE -- do not edit by hand.
 *
 * Produced by packages/data/src/derive-baselines.mjs from 1243 Cricsheet
 * IPL matches. Re-run that script when a new season is added.
 *
 * These are measured facts about real cricket, not tuning parameters. They set
 * the base rates the ball model bends with player ratings, and they are the
 * targets the calibration harness asserts against.
 */

export type Phase = 'POWERPLAY' | 'MIDDLE' | 'DEATH';

/** Runs off the bat on a legal delivery. 5s are folded into 4. */
export type RunOutcome = 0 | 1 | 2 | 3 | 4 | 6;

export interface PhaseBaseline {
  /** Sample size behind these rates. */
  readonly legalBalls: number;
  /** P(wicket) per legal ball. */
  readonly wicketRate: number;
  readonly ballsPerWicket: number;
  /** P(runs off bat | legal ball, not a wicket). Sums to 1. Model input. */
  readonly runs: Readonly<Record<RunOutcome, number>>;
  /**
   * Unconditional measured rates over every legal delivery, including extras
   * and wicket balls. These are the harness targets and are directly
   * comparable to published IPL figures -- deliberately NOT the same
   * quantities as `runs` above, which is conditional on surviving the ball.
   */
  readonly observed: {
    readonly runsPerOver: number;
    readonly dotRate: number;
    readonly fourRate: number;
    readonly sixRate: number;
  };
  /** Rates per legal ball, so wides are additional deliveries. */
  readonly extrasPerLegalBall: {
    readonly wides: number;
    readonly noballs: number;
    readonly byes: number;
    readonly legbyes: number;
  };
  /** P(mode | wicket). Retirements excluded; sums to 1. */
  readonly dismissalModes: Readonly<Record<string, number>>;
}

export const PHASES = ['POWERPLAY', 'MIDDLE', 'DEATH'] as const;

/** Over index (0-based) to phase. Powerplay 1-6, middle 7-15, death 16-20. */
export function phaseOf(overIndex: number): Phase {
  return overIndex < 6 ? 'POWERPLAY' : overIndex < 15 ? 'MIDDLE' : 'DEATH';
}

export const BASELINES: Readonly<Record<Phase, PhaseBaseline>> = {
  "POWERPLAY": {
    "legalBalls": 89318,
    "wicketRate": 0.04035,
    "ballsPerWicket": 24.783,
    "runs": {
      "0": 0.459913,
      "1": 0.278811,
      "2": 0.044462,
      "3": 0.004503,
      "4": 0.168397,
      "6": 0.043913
    },
    "observed": {
      "runsPerOver": 8.095,
      "dotRate": 0.462482,
      "fourRate": 0.161009,
      "sixRate": 0.042142
    },
    "extrasPerLegalBall": {
      "wides": 0.03604,
      "noballs": 0.004109,
      "byes": 0.001791,
      "legbyes": 0.017231
    },
    "dismissalModes": {
      "caught": 0.653078,
      "bowled": 0.178314,
      "lbw": 0.07543,
      "run out": 0.055186,
      "caught and bowled": 0.023017,
      "stumped": 0.013588,
      "hit wicket": 0.001109,
      "obstructing the field": 0.000277
    }
  },
  "MIDDLE": {
    "legalBalls": 131526,
    "wicketRate": 0.043208,
    "ballsPerWicket": 23.144,
    "runs": {
      "0": 0.303132,
      "1": 0.476165,
      "2": 0.069595,
      "3": 0.00236,
      "4": 0.097383,
      "6": 0.051366
    },
    "observed": {
      "runsPerOver": 7.951,
      "dotRate": 0.317633,
      "fourRate": 0.093061,
      "sixRate": 0.049161
    },
    "extrasPerLegalBall": {
      "wides": 0.028618,
      "noballs": 0.002775,
      "byes": 0.001908,
      "legbyes": 0.012978
    },
    "dismissalModes": {
      "caught": 0.629428,
      "bowled": 0.16275,
      "lbw": 0.068572,
      "run out": 0.061207,
      "stumped": 0.043494,
      "caught and bowled": 0.033672,
      "hit wicket": 0.000877
    }
  },
  "DEATH": {
    "legalBalls": 63786,
    "wicketRate": 0.083874,
    "ballsPerWicket": 11.923,
    "runs": {
      "0": 0.249435,
      "1": 0.425851,
      "2": 0.097354,
      "3": 0.002721,
      "4": 0.131323,
      "6": 0.093316
    },
    "observed": {
      "runsPerOver": 10.06,
      "dotRate": 0.284467,
      "fourRate": 0.12023,
      "sixRate": 0.085505
    },
    "extrasPerLegalBall": {
      "wides": 0.045355,
      "noballs": 0.007745,
      "byes": 0.005236,
      "legbyes": 0.018186
    },
    "dismissalModes": {
      "caught": 0.62863,
      "bowled": 0.165488,
      "run out": 0.120253,
      "lbw": 0.040953,
      "caught and bowled": 0.02513,
      "stumped": 0.01694,
      "hit wicket": 0.002048,
      "obstructing the field": 0.000558
    }
  }
} as const;

/**
 * Whole-match targets for the calibration harness. A league-average XI against
 * a league-average attack must reproduce these.
 */
export const MATCH_TARGETS = {
  "meanFirstInningsScore": 170.76,
  "medianFirstInningsScore": 169,
  "chaseWinRate": 0.546798,
  "firstInningsSample": 1200
} as const;

/**
 * Cricsheet does not tag wicketkeepers in its IPL fielder records, so the
 * keeper's share of catches cannot be measured from the archive. This is the
 * accepted figure for T20 cricket and is applied when assigning credit for a
 * catch to a specific fielder.
 */
export const KEEPER_SHARE_OF_CATCHES = 0.18;
